import StaticPage from '../_StaticPage'

// Revised September 2026. Every statement here is checked against the code:
// the cookies are the three the site sets plus the Rails session cookie
// (LocaleRedirect, _stations.jsx, _exploreLayers.js); the server-side usage
// pipeline records no IP, cookie or user agent (StationAnalytics::Recorder);
// IP-derived location is CloudFront's viewer headers on the no-coordinate
// lookup only (CloudfrontViewerLocation) and is not stored; Go Mode is
// foreground-only (_useMapLocation.js, _useWakeLock.js). Keep it that way when
// the code changes. See docs/context/project_legal_pages_refresh_2026_09.md.
export default function Privacy() {
  return (
    <StaticPage
      title="Privacy Policy"
      description="Privacy Policy for Tides Near Me - how the tide, current, marine weather and nautical chart app and website handle your data."
    >
      <p>
        <b>Last updated:</b> September 2026
      </p>
      <p>
        Tides Near Me ("we", "our", or "us") respects your privacy. This Privacy Policy explains how
        we collect, use, and protect information when you use the Tides Near Me mobile applications
        and website (collectively, the "Services").
      </p>
      <h3>In short</h3>
      <ul>
        <li>The Services have no user accounts. We do not ask for your name or email address.</li>
        <li>
          Your location is used to show nearby stations and, in Go Mode, to follow your position on
          the map. It is processed on your device and is not stored by us.
        </li>
        <li>
          We do not sell personal information, and we do not use advertising or cross-site tracking
          cookies on the website.
        </li>
        <li>
          Favorites, recent stations, and preferences are stored on your device, not on our servers.
        </li>
      </ul>
      <hr />
      <h2>Information We Collect</h2>
      <p>
        We may collect or receive the following categories of information when you use the Services.
      </p>
      <h3>1. Device and Usage Information</h3>
      <ul>
        <li>App or browser type, operating system, and basic device information</li>
        <li>
          Usage data, such as pages or screens viewed, features used, and interaction events. In the
          mobile apps this includes a per-installation analytics identifier assigned by our mobile
          analytics provider; on the website it does not include any identifier.
        </li>
        <li>Crash reports and diagnostic information</li>
        <li>
          Server logs. Like most online services, our servers and content delivery network record
          the IP address, requested URL, browser type, and time of each request. These logs are used
          for security, rate limiting, and troubleshooting, and are kept for up to 90 days.
        </li>
      </ul>
      <p>
        This information is used to operate the Services, improve performance, and diagnose
        technical issues.
      </p>
      <hr />
      <h3>2. Location Information</h3>
      <p>Location may reach the Services in three ways:</p>
      <ul>
        <li>
          <b>Device location you grant.</b> If you allow location access, the Services use your
          device's reported position to show nearby tide and current stations and to position the
          map. In <b>Go Mode</b>, while it is open, the Services read your position, heading, and
          speed continuously to follow you on the map and to show the depth and conditions at your
          position. This is processed on your device. Your coordinates are sent to our servers only
          to look up nearby stations, and are not stored.
        </li>
        <li>
          <b>Approximate location from your IP address.</b> If you have not granted location access,
          our content delivery network may infer an approximate location (typically city level) from
          your IP address so the website can open on a relevant area. This inferred location is used
          for that purpose only and is not stored.
        </li>
        <li>
          <b>A location you choose</b>, such as a place you search for or a point you select on the
          map.
        </li>
      </ul>
      <p>
        We do not track your location in the background. Location access ends when you close Go Mode
        or leave the map, and you can withdraw permission at any time in your device or browser
        settings. When you view the map, the map, chart, and weather tiles your device requests
        reveal the general area you are looking at to the servers that deliver them, as with any
        online map.
      </p>
      <hr />
      <h3>3. Search Queries</h3>
      <p>
        Station search is provided by a third-party search service. The text you type, and where
        applicable your approximate location for ranking, are sent from your device directly to that
        service together with your IP address. Search queries are not linked to any account or
        identifier.
      </p>
      <hr />
      <h3>4. Subscription and Purchase Information</h3>
      <p>If you choose to purchase a subscription through the mobile apps:</p>
      <ul>
        <li>
          Payments are processed by the platform provider (
          <b>Apple App Store or Google Play Store</b>) using their respective in-app purchase
          systems
        </li>
        <li>
          Subscription status is managed for us by <b>RevenueCat</b>, which receives the purchase
          receipt or purchase token from the platform, a random per-installation identifier, your
          device type and operating system, and the last time the app was used. RevenueCat processes
          this data on our behalf and does not use it for its own purposes.
        </li>
        <li>
          We do <b>not</b> receive or store your payment method, credit card number, or billing
          details
        </li>
      </ul>
      <p>
        Subscriptions are <b>not offered through the website</b>.
      </p>
      <p>
        This information is used solely to determine access to premium features in the mobile apps.
      </p>
      <hr />
      <h3>5. Advertising Information</h3>
      <p>
        The free version of the mobile apps displays advertisements served by <b>Google AdMob</b>.
        The website may also display advertisements. Google, as a third-party vendor, uses
        advertising identifiers and similar technologies to serve ads based on your use of the apps
        and of other apps and websites, and to measure and report on them. When ads are shown,
        Google may collect:
      </p>
      <ul>
        <li>Advertising identifiers (such as IDFA or GAID, where permitted)</li>
        <li>IP address and coarse location derived from it</li>
        <li>Device and app information, and ad interaction data</li>
      </ul>
      <p>
        How Google uses this information is described at{' '}
        <a href="https://policies.google.com/technologies/partner-sites">
          policies.google.com/technologies/partner-sites
        </a>
        . You can opt out of personalized advertising in{' '}
        <a href="https://www.google.com/settings/ads">Google Ads Settings</a>, at{' '}
        <a href="https://www.aboutads.info/choices/">aboutads.info</a>, or through your device's
        privacy and advertising settings. If you are in the European Economic Area, the United
        Kingdom, or Switzerland, the apps ask for your consent before personalized ads are shown,
        and you can change that choice at any time in the app's settings.
      </p>
      <hr />
      <h2>Cookies and Local Storage</h2>
      <p>The website sets a small number of first-party cookies, none of which identifies you:</p>
      <ul>
        <li>
          <b>locale</b> - remembers that your language was detected on your first visit, so you are
          not redirected again
        </li>
        <li>
          <b>tnm_countries</b> and <b>tnm_layers</b> - remember your choices for the countries list
          and the map layers so the page renders correctly on load
        </li>
        <li>A standard session cookie required for the website to function</li>
      </ul>
      <p>
        The website also uses your browser's local storage to keep favorites, recent stations, unit
        preferences, and map settings on your device. Nothing in local storage is sent to our
        servers or synced between devices. There are no advertising or cross-site tracking cookies
        on the website, and no cookie consent banner is needed. You can clear cookies and local
        storage at any time in your browser settings.
      </p>
      <hr />
      <h2>How We Use Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide tide and current predictions, marine weather, charts, and related features</li>
        <li>Display nearby stations and position the map</li>
        <li>Manage subscription access and entitlements (mobile apps only)</li>
        <li>Understand which stations, pages, and features are used, in aggregate</li>
        <li>Improve functionality, performance, and reliability</li>
        <li>Diagnose crashes and technical issues, and protect the Services from abuse</li>
        <li>Display advertisements in the free mobile apps</li>
      </ul>
      <hr />
      <h2>Analytics</h2>
      <ul>
        <li>
          <b>Website analytics</b> use a privacy-focused, cookieless service that reports aggregate
          page views and events without identifying individual visitors.
        </li>
        <li>
          <b>Usage metrics</b> recorded by our own servers describe which station or page was
          requested and how the request performed. They deliberately contain no IP address, cookie,
          user agent, coordinates, or other identifier, and cannot be used to count or follow a
          person.
        </li>
        <li>
          <b>Mobile app analytics</b> use Google Firebase and our own event pipeline to record
          screens viewed and features used, associated with a per-installation analytics identifier.
          This data is stored in Google BigQuery.
        </li>
      </ul>
      <hr />
      <h2>Third-Party Service Providers</h2>
      <p>
        We use third-party services to operate the Services. Each receives only what it needs to do
        its job:
      </p>
      <ul>
        <li>
          <b>Hosting and content delivery:</b> Heroku (Salesforce) and Amazon Web Services,
          including Amazon CloudFront
        </li>
        <li>
          <b>Map tiles:</b> OpenFreeMap, which serves the base map to your device and therefore
          receives your IP address and the map area you view
        </li>
        <li>
          <b>Search:</b> Algolia
        </li>
        <li>
          <b>Website analytics:</b> Plausible Analytics
        </li>
        <li>
          <b>Mobile app analytics and data storage:</b> Google Firebase and Google BigQuery
        </li>
        <li>
          <b>In-app purchases:</b> Apple App Store (iOS) and Google Play Billing (Android), with
          subscription status managed by RevenueCat (
          <a href="https://www.revenuecat.com/privacy/">privacy policy</a>)
        </li>
        <li>
          <b>Advertising:</b> Google AdMob in the free mobile apps (
          <a href="https://policies.google.com/privacy">privacy policy</a>)
        </li>
      </ul>
      <p>
        These third parties may collect and process information according to their own privacy
        policies. We encourage you to review those policies. The data providers whose tide, chart,
        and weather data we display (listed on our <a href="/data-sources">Data Sources</a> page)
        receive no information about you; we fetch their data ourselves.
      </p>
      <hr />
      <h2>In-App Purchases and Subscriptions</h2>
      <p>The mobile apps offer optional auto-renewable subscriptions.</p>
      <ul>
        <li>Subscriptions are managed by the platform provider (Apple or Google)</li>
        <li>Payment is charged to your Apple ID or Google account, depending on your device</li>
        <li>We receive subscription status information to unlock premium features</li>
        <li>We do not store or process payment information</li>
      </ul>
      <p>
        You can manage or cancel subscriptions through your Apple ID account settings or Google Play
        account settings.
      </p>
      <p>
        The website does <b>not</b> process payments or offer subscriptions.
      </p>
      <hr />
      <h2>Data Retention</h2>
      <p>We retain information only for as long as necessary to:</p>
      <ul>
        <li>Provide the Services</li>
        <li>Comply with legal obligations</li>
        <li>Resolve disputes</li>
        <li>Enforce our agreements</li>
      </ul>
      <p>
        Server logs are kept for up to 90 days and then deleted. Aggregate usage metrics that
        contain no identifiers may be kept indefinitely.
      </p>
      <hr />
      <h2>International Transfers</h2>
      <p>
        We are based in the United States and our servers are located there. If you use the Services
        from outside the United States, the information described above is processed in the United
        States. Where required, we rely on our providers' standard contractual safeguards for those
        transfers.
      </p>
      <hr />
      <h2>Legal Basis (EEA, UK, and Switzerland)</h2>
      <p>
        Where these laws apply, we process device and usage information, server logs, and aggregate
        analytics on the basis of our legitimate interest in operating, securing, and improving the
        Services. Device location is processed only with your consent, which you give through your
        device or browser permission prompt and can withdraw at any time.
      </p>
      <hr />
      <h2>Your Choices and Rights</h2>
      <p>Depending on your location, you may have rights to:</p>
      <ul>
        <li>Access the personal information we hold</li>
        <li>Request correction or deletion of your information</li>
        <li>Object to or restrict certain processing</li>
        <li>Opt out of personalized advertising</li>
        <li>Lodge a complaint with your local data protection authority</li>
      </ul>
      <p>
        Because the Services have no accounts, we generally hold no information that identifies you,
        and most choices are made directly on your device: you can manage location access and
        advertising preferences in your device or browser settings, and clear favorites and
        preferences by clearing the app's or browser's storage. To exercise any right, contact us at
        the address below.
      </p>
      <p>
        <b>California residents:</b> we do not sell personal information and do not share it for
        cross-context behavioral advertising.
      </p>
      <hr />
      <h2>Security</h2>
      <p>
        We take reasonable administrative and technical measures to protect information against
        unauthorized access, alteration, disclosure, or destruction. However, no method of
        transmission or storage is completely secure.
      </p>
      <hr />
      <h2>Children's Privacy</h2>
      <p>
        The Services are not directed to children under the age of 13 (or the equivalent minimum age
        in your jurisdiction), and we do not knowingly collect personal information from children.
      </p>
      <hr />
      <h2>Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Any changes will be posted on this page
        with an updated revision date.
      </p>
      <hr />
      <h2>Contact Us</h2>
      <p>If you have questions about this Privacy Policy, you may contact us at:</p>
      <ul>
        <li>
          <b>Email:</b> <a href="mailto:support@tidesnear.me">support@tidesnear.me</a>
        </li>
        <li>
          <b>Website:</b> <a href="https://tidesnear.me">https://tidesnear.me</a>
        </li>
      </ul>
    </StaticPage>
  )
}
